/**
 * Browser-side API client. Calls the Control Plane at `api.{base}` with
 * the `kin_session` cookie scoped to `.{base}` (set by the auth-worker
 * on a successful Google Workspace OIDC handshake). No auth code lives
 * in the SPA.
 *
 * The base origin is derived at runtime from the current Portal hostname:
 *   - Portal at `dev.mkin.app` → API at `api.dev.mkin.app`
 *   - Portal at `mkin.app` → API at `api.mkin.app`
 *
 * Same-site cookie sharing (both at `.mkin.app`) means `credentials:
 * 'include'` is enough — the browser auto-attaches the cookie. The
 * auth-worker fronts `api.*` too, so on a stale session the auth-worker
 * returns its own 302 to `auth.{base}/login` before the request reaches
 * the Control Plane.
 */

import type {
  BearerPathsResponse,
  CreateExternalAppResponse,
  DependenciesResponse,
  MintTokenResponse,
} from './types.js';

export function apiOrigin(): string {
  if (typeof window === 'undefined') return 'https://api.mkin.app';
  const host = window.location.hostname;
  // Strip a leading subdomain if present so dev.mkin.app → api.dev.mkin.app
  // and mkin.app → api.mkin.app. We just prepend "api." in both cases.
  return `https://api.${host}`;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Extract a user-facing string from anything thrown out of an API call.
 * ApiError already carries the server's preferred message; everything else
 * falls back to String(err). Replaces the same five-token expression
 * repeated 30+ times across the per-app pages.
 */
export function apiMessage(err: unknown): string {
  return err instanceof ApiError ? err.message : String(err);
}

async function request<T>(
  path: string,
  init: RequestInit & { method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' } = {},
): Promise<T> {
  const url = `${apiOrigin()}${path}`;
  let res: Response;
  try {
    res = await fetch(url, {
      ...init,
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
        ...(init.headers ?? {}),
      },
    });
  } catch (err) {
    // The auth-worker bounces unauthenticated requests with a 302 to its
    // own login page; cross-origin redirects without CORS headers cause
    // `fetch` to throw a TypeError ("Load failed" in Safari, "Failed to
    // fetch" in Chrome). Treat as unauthenticated — the caller's
    // top-level navigation to `auth.{base}/login` recovers properly.
    throw new ApiError('Not signed in', 401, { cause: String(err) });
  }

  if (res.status === 401) {
    // Unauthenticated → caller is expected to navigate the browser to
    // `auth.{base}/login?return_to=<current>` so the auth-worker mints a
    // fresh kin_session cookie. The SPA raises so callers can render
    // a "redirecting" state.
    throw new ApiError('Not signed in', 401);
  }
  if (res.status === 403) {
    // Authenticated but not authorized — surface the server's detail
    // when present (e.g. "manager_required"), else a friendly default.
    // Critically, do NOT collapse this into "Not signed in": a 403 means
    // the session is valid, sending the user to /login would re-auth and
    // bounce them right back to the same denial. Caller renders this
    // inline (e.g. "Couldn't change visibility: Access denied").
    let detail: string | null = null;
    try {
      const body = (await res.json()) as { detail?: unknown; message?: unknown };
      if (typeof body.message === 'string') detail = body.message;
      else if (typeof body.detail === 'string') detail = body.detail;
    } catch {
      // body wasn't JSON — fall back to the generic message.
    }
    throw new ApiError(detail ? `Access denied (${detail})` : 'Access denied', 403);
  }

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (!res.ok) {
    // Surface the server's human-readable detail (set on most error
    // responses) on top of the canonical `error` slug, so the UI banner
    // shows something actionable like "locked — Another deploy or
    // publish is in progress. Try again in a few seconds." instead of
    // a bare "locked". Check both `message` and `detail` because the
    // server uses both spellings depending on the route. `message` wins
    // when both are present — that's the convention non-prod analytics
    // errors use, and they're already shaped for the UI.
    const baseMessage =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : `HTTP ${res.status}`;
    let detail: string | null = null;
    if (body && typeof body === 'object') {
      if ('message' in body && typeof (body as { message?: unknown }).message === 'string') {
        detail = (body as { message: string }).message;
      } else if ('detail' in body && typeof (body as { detail?: unknown }).detail === 'string') {
        detail = (body as { detail: string }).detail;
      }
    }
    const message = detail ? `${baseMessage} — ${detail}` : baseMessage;
    throw new ApiError(message, res.status, body);
  }
  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(
      path,
      body !== undefined ? { method: 'POST', body: JSON.stringify(body) } : { method: 'POST' },
    ),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(
      path,
      body !== undefined ? { method: 'PATCH', body: JSON.stringify(body) } : { method: 'PATCH' },
    ),
  put: <T>(path: string, body?: unknown) =>
    request<T>(
      path,
      body !== undefined ? { method: 'PUT', body: JSON.stringify(body) } : { method: 'PUT' },
    ),
  delete: <T>(path: string, body?: unknown) =>
    request<T>(
      path,
      body !== undefined ? { method: 'DELETE', body: JSON.stringify(body) } : { method: 'DELETE' },
    ),
};

// -------------------- Typed control-plane helpers --------------------------
//
// Thin, typed wrappers over the generic `api.*` verbs for the routes whose
// request/response shapes are stable enough to name. Call sites get the
// payload typed without re-stating the path + generic at every use, and
// tests can mock these by name. Errors (400/409/403) propagate as ApiError —
// callers map them to inline copy via `apiMessage`.

/** Register an external app (platform-admin only — server enforces). */
export function createExternalApp(input: {
  slug: string;
  name: string;
  region: string;
  external_hostnames: string[];
}): Promise<CreateExternalAppResponse> {
  return api.post<CreateExternalAppResponse>('/api/apps/external', input);
}

/** Mint a one-time `kin_apt_` bearer token for an app (manager-only). */
export function mintAppToken(
  slug: string,
  input: { label: string; expiresInDays?: number },
): Promise<MintTokenResponse> {
  return api.post<MintTokenResponse>(`/api/apps/${encodeURIComponent(slug)}/tokens`, input);
}

/** Replace an app's bearer-path allowlist (manager-only). */
export function setBearerPaths(slug: string, paths: string[]): Promise<BearerPathsResponse> {
  return api.put<BearerPathsResponse>(`/api/apps/${encodeURIComponent(slug)}/bearer-paths`, {
    paths,
  });
}

// -------------------- Inter-app dependencies -------------------------------

/** Both directions of an app's dependency graph (`inbound` is manager-only). */
export function listDependencies(slug: string): Promise<DependenciesResponse> {
  return api.get<DependenciesResponse>(`/api/apps/${encodeURIComponent(slug)}/dependencies`);
}

/**
 * Declare that `slug` may call `depends_on_slug` (manager-only). Pending
 * until a manager of the target approves — unless the actor manages both,
 * in which case the response comes back `approved: true`.
 */
export function addDependency(
  slug: string,
  input: { depends_on_slug: string },
): Promise<{ id: string; target_slug: string; binding_name: string; approved: boolean }> {
  return api.post(`/api/apps/${encodeURIComponent(slug)}/dependencies`, input);
}

/** Consent to an inbound edge — `slug` is the TARGET (manager-only). */
export function approveInboundDependency(
  slug: string,
  callerSlug: string,
): Promise<{ approved: boolean; alreadyApproved: boolean }> {
  return api.post(
    `/api/apps/${encodeURIComponent(slug)}/dependencies/inbound/${encodeURIComponent(callerSlug)}/approve`,
  );
}

/** Drop one of `slug`'s own outbound edges (manager-only). */
export function removeDependency(slug: string, targetSlug: string): Promise<{ removed: boolean }> {
  return api.delete(
    `/api/apps/${encodeURIComponent(slug)}/dependencies/${encodeURIComponent(targetSlug)}`,
  );
}

/** Revoke an inbound edge — `slug` is the TARGET withdrawing access (manager-only). */
export function revokeInboundDependency(
  slug: string,
  callerSlug: string,
): Promise<{ removed: boolean }> {
  return api.delete(
    `/api/apps/${encodeURIComponent(slug)}/dependencies/inbound/${encodeURIComponent(callerSlug)}`,
  );
}
